import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { getErrorMessage } from "utils/error";
import { writeClipboardTextFromPromise } from "utils/handleCopy";
import { ContactFormsMenuTrigger } from "./ContactFormsMenuTrigger";
import { useEligibleContactFormSources } from "./useEligibleContactFormSources";
import { ContactFormsSubmenuVariant } from "./types";

interface ContactFormsSubmenuProps {
  variant?: ContactFormsSubmenuVariant;
  disabled?: boolean;
  // The scope of the generated link, narrowest wins — see buildLinkScopeBody:
  //  - neither id: portfolio-scoped (the portfolio page), visitor picks freely;
  //  - propertyId: scoped to that property/community — the public form
  //    pre-selects it, disables the picker and skips the captcha (CRM-939);
  //  - unitId: scoped to that unit/site — the public form locks both the
  //    property and the unit, and inherits the same captcha skip because a
  //    unit-scoped link also carries its property (CRM-1091).
  propertyId?: number;
  unitId?: number;
}

interface CreateLinkResponse {
  url: string;
}

// The two ids are mutually exclusive on the wire, so the narrower scope wins.
const buildLinkScopeBody = ({
  propertyId,
  unitId
}: {
  propertyId?: number;
  unitId?: number;
}) => {
  if (unitId) return { unit_id: unitId };
  if (propertyId) return { property_id: propertyId };
  return {};
};

export const ContactFormsSubmenu = ({
  variant = "FULL",
  disabled,
  propertyId,
  unitId
}: ContactFormsSubmenuProps) => {
  const { eligibleSources, isLoading } = useEligibleContactFormSources();

  const { mutateAsync: generateLink, isPending } = useMutation({
    mutationKey: ["LEAD_SOURCE_CONTACT_FORM_LINK_CREATE"],
    mutationFn: async ({ leadSourceId }: { leadSourceId: number }) => {
      const { data } = await instance.post<CreateLinkResponse>(
        `/portal/v1/boom_crm/lead_sources/${leadSourceId}/contact_form_links`,
        buildLinkScopeBody({ propertyId, unitId })
      );
      return data;
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  // Must run synchronously in the click handler: Safari revokes the click's
  // transient user activation after an await, so the pending URL promise is
  // handed to the clipboard instead of being awaited first.
  const copyContactFormLink = (leadSourceId: number) => {
    let requestFailed = false;
    const urlPromise = generateLink({ leadSourceId })
      .then(({ url }) => url)
      .catch((error) => {
        requestFailed = true;
        throw error;
      });

    writeClipboardTextFromPromise(urlPromise)
      .then(() => toast.info("Link copied"))
      .catch((error) => {
        // Request errors are already toasted by the mutation's onError.
        if (!requestFailed) toast.error(getErrorMessage(error));
      });
  };

  const headerAction = {
    title: <p className="text-sm font-medium text-content-400">Lead source</p>,
    className: "!cursor-default !bg-white hover:!bg-white !py-2",
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
    }
  };

  const sourceActions = eligibleSources.map((source) => ({
    title: (
      <div className="flex items-center gap-3 h-8">
        <span
          className="inline-block size-4 rounded-full shrink-0"
          style={{ backgroundColor: source.color ?? undefined }}
        />
        <h2 className="text-base font-medium text-content-1000 truncate">
          {source.name}
        </h2>
      </div>
    ),
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      copyContactFormLink(source.id);
    }
  }));

  return (
    <ContextMenuPopover
      placement={variant === "BUTTON" ? "bottom-end" : "left-start"}
      disabled={disabled || isLoading || isPending}
      contentClassName={
        variant === "BUTTON"
          ? "[&>div]:!min-w-[182px] [&>div]:!w-[182px]"
          : "[&>div]:!-translate-x-4 [&>div]:!min-w-[182px] [&>div]:!w-[182px]"
      }
      cbTrigger={() => (
        <ContactFormsMenuTrigger variant={variant} disabled={disabled} />
      )}
      actions={[headerAction, ...sourceActions]}
    />
  );
};
